import { useStrings } from "@1js/localization-components";
import type { Prompt } from "@1js/pl-card-components";
import { PromptAuthor } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import {
  makeStyles,
  shorthands,
  Text,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import type { WebPrompt } from "../../data";
import { MoreLikeThis } from "../MoreLikeThis.web";
import { PromptDescription } from "../PromptDescription.web";
import { Detail } from "./Detail.web";
import { MakeItYourOwn } from "./MakeItYourOwn";
import { AuthorLabel } from "./PromptPreview.web.string";

const useStyles = makeStyles({
  mainContainer: {
    display: "flex",
    width: "725px",
    flexDirection: "column",
    ...shorthands.gap("16px"),
    "@media (max-width: 540px)": {
      width: "auto",
      justifyContent: "center",
      alignItems: "flex-start",
      alignSelf: "stretch",
      ...shorthands.padding("0px"),
      ...shorthands.margin("2px", "2px"),
    },
  },

  authorContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    alignSelf: "stretch",
    ...shorthands.gap("8px"),
  },

  author: {
    color: tokens.colorNeutralForeground2,
    fontSize: "0.75rem",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "1.375rem",
    alignContent: "end",
  },
});

type PromptPreviewProps = {
  prompt: WebPrompt;
  morePrompts: Prompt[];
  isBookmarked?: boolean;
  isBookmarkEnabled?: boolean;
  isPromptSavingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isShowPromptAuthor?: boolean;
  isPromptCopyLinkEnabled?: boolean;
  onBookmarkToggled?: (prompt: Prompt, value: boolean) => Promise<void>;
  onShareClicked?: (prompt: Prompt) => void;
  onCopyClicked?: (prompt: Prompt) => void;
  onClick: (prompt: Prompt) => void;
  onDelete?: (prompt: Prompt) => void;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isPromptSharingToWorkgroupEnabled?: boolean;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void | Promise<void>;
  groups?: Group[];
};

export const PromptPreview: React.FC<PromptPreviewProps> = ({
  prompt,
  morePrompts,
  isBookmarked,
  isBookmarkEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isShowPromptAuthor,
  isPromptCopyLinkEnabled,
  onBookmarkToggled,
  onShareClicked,
  onCopyClicked,
  onClick,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    AuthorLabel,
  });
  const showMakeItYourOwn =
    !prompt.IsUserPrompt &&
    prompt.DisplayDescription &&
    prompt.DisplayDescription.length > 0;

  return (
    <div className={styles.mainContainer}>
      {(isPromptSavingEnabled || isShowPromptAuthor) &&
        !(
          (prompt.IsUserPrompt || prompt.Origin === "User") &&
          !prompt.Author
        ) && (
          <div className={styles.authorContainer}>
            <Text className={styles.author}>{localizations.AuthorLabel()}</Text>
            <PromptAuthor prompt={prompt} expanded={true} />
          </div>
        )}
      <Detail
        prompt={prompt}
        isBookmarkEnabled={isBookmarkEnabled}
        isPromptSavingEnabled={isPromptSavingEnabled}
        isPromptSharingEnabled={isPromptSharingEnabled}
        isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
        isBookmarked={isBookmarked}
        isPromptCopyLinkEnabled={isPromptCopyLinkEnabled}
        onBookmarkToggled={onBookmarkToggled}
        onShareClicked={onShareClicked}
        onCopyClicked={onCopyClicked}
        onDelete={onDelete}
        onTryInAppBadgeAction={onTryInAppBadgeAction}
        isPromptSharingToWorkgroupEnabled={isPromptSharingToWorkgroupEnabled}
        onShareToWorkgroupClicked={onShareToWorkgroupClicked}
        groups={groups}
      />
      <PromptDescription prompt={prompt} />
      {showMakeItYourOwn && prompt.DisplayDescription && (
        <MakeItYourOwn text={prompt.DisplayDescription} />
      )}
      <MoreLikeThis
        prompts={morePrompts}
        onClicked={onClick}
        onBookmarkToggled={onBookmarkToggled}
      />
    </div>
  );
};
